import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { keyframes } from '@mui/material/styles';
import { SceneContainer } from './shared';

// --- Keyframe animations ---

const carRace = keyframes`
  0% { transform: translateX(-120px); }
  40% { transform: translateX(40px); }
  50% { transform: translateX(40px); }
  90% { transform: translateX(160px); }
  91% { transform: translateX(-120px); }
  100% { transform: translateX(-120px); }
`;

const wheelSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const speedLine = keyframes`
  0% { opacity: 0; transform: translateX(0) scaleX(0.3); }
  30% { opacity: 0.6; transform: translateX(-20px) scaleX(1); }
  100% { opacity: 0; transform: translateX(-60px) scaleX(0.5); }
`;

const sparkle = keyframes`
  0% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
  100% { opacity: 0; transform: scale(0) rotate(360deg); }
`;

const controllerPulse = keyframes`
  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 4px rgba(168, 85, 247, 0.3)); }
  50% { transform: scale(1.05); filter: drop-shadow(0 0 12px rgba(168, 85, 247, 0.6)); }
`;

const buttonBlink = keyframes`
  0%, 70%, 100% { opacity: 0.4; }
  80% { opacity: 1; }
`;

const neonFlicker = keyframes`
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
  52% { opacity: 0.6; }
  54% { opacity: 1; }
`;

const textGlow = keyframes`
  0%, 100% { text-shadow: 0 0 4px rgba(168, 85, 247, 0.4); }
  50% { text-shadow: 0 0 12px rgba(168, 85, 247, 0.8), 0 0 20px rgba(168, 85, 247, 0.3); }
`;

const COLORS = {
  purple: '#A855F7',
  purpleDark: '#9333EA',
  purpleLight: '#C084FC',
  purpleText: '#7C3AED', // WCAG AA compliant on white (~4.6:1)
  cyan: '#06B6D4',
  pink: '#EC4899',
  orange: '#F97316',
  neonGreen: '#22D3EE',
  dark: '#1E1033',
};

interface GamingLoadingAnimationProps {
  message?: string;
}

const GamingLoadingAnimationBase: React.FC<GamingLoadingAnimationProps> = ({
  message = 'Loading your game...',
}) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <SceneContainer>
      {/* Sparkle particles */}
      {[
        { top: '10%', left: '15%', delay: '0s', color: COLORS.cyan, size: 14 },
        { top: '20%', left: '80%', delay: '0.4s', color: COLORS.purple, size: 12 },
        { top: '60%', left: '10%', delay: '0.8s', color: COLORS.pink, size: 10 },
        { top: '15%', left: '50%', delay: '1.2s', color: COLORS.orange, size: 11 },
        { top: '70%', left: '85%', delay: '0.6s', color: COLORS.neonGreen, size: 13 },
        { top: '45%', left: '90%', delay: '1.5s', color: COLORS.purpleLight, size: 10 },
        { top: '75%', left: '20%', delay: '1.8s', color: COLORS.cyan, size: 12 },
      ].map((s, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: s.top,
            left: s.left,
            animation: `${sparkle} 2s ease-in-out ${s.delay} infinite`,
          }}
        >
          <svg width={s.size} height={s.size} viewBox="0 0 24 24" fill={s.color}>
            <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z" />
          </svg>
        </Box>
      ))}

      {/* Main scene SVG */}
      <svg
        width="300"
        height="190"
        viewBox="0 0 300 190"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="presentation"
        style={{ maxWidth: '100%', height: 'auto' }}
      >
        {/* Game controller in center */}
        <g style={{ animation: `${controllerPulse} 2.5s ease-in-out infinite` }}>
          {/* Controller body */}
          <rect x="100" y="55" width="100" height="55" rx="16" fill={COLORS.purple} />

          {/* Left grip */}
          <rect x="90" y="72" width="22" height="38" rx="10" fill={COLORS.purple} />
          {/* Right grip */}
          <rect x="188" y="72" width="22" height="38" rx="10" fill={COLORS.purple} />

          {/* Left joystick */}
          <circle cx="130" cy="73" r="10" fill={COLORS.purpleDark} />
          <circle cx="130" cy="73" r="6" fill={COLORS.dark} />

          {/* Right joystick */}
          <circle cx="170" cy="85" r="10" fill={COLORS.purpleDark} />
          <circle cx="170" cy="85" r="6" fill={COLORS.dark} />

          {/* D-pad */}
          <rect x="121" y="88" width="18" height="6" rx="1.5" fill={COLORS.purpleDark} />
          <rect x="127" y="82" width="6" height="18" rx="1.5" fill={COLORS.purpleDark} />

          {/* Action buttons - blinking */}
          <g>
            <circle cx="170" cy="68" r="4.5" fill={COLORS.cyan}
              style={{ animation: `${buttonBlink} 1.5s ease-in-out 0s infinite` }} />
            <circle cx="162" cy="76" r="4.5" fill={COLORS.pink}
              style={{ animation: `${buttonBlink} 1.5s ease-in-out 0.2s infinite` }} />
            <circle cx="178" cy="76" r="4.5" fill={COLORS.neonGreen}
              style={{ animation: `${buttonBlink} 1.5s ease-in-out 0.4s infinite` }} />
            <circle cx="170" cy="84" r="4.5" fill={COLORS.orange}
              style={{ animation: `${buttonBlink} 1.5s ease-in-out 0.6s infinite` }} />
          </g>

          {/* Center button / logo area */}
          <rect x="139" y="73" width="22" height="8" rx="4" fill={COLORS.purpleDark} />
          <circle cx="150" cy="77" r="2" fill={COLORS.cyan} opacity={0.8} />
        </g>

        {/* Racing car below controller */}
        <g style={{ animation: `${carRace} 4s ease-in-out infinite` }}>
          {/* Car body */}
          <rect x="120" y="138" width="55" height="16" rx="5" fill={COLORS.cyan} />
          <rect x="130" y="130" width="30" height="12" rx="4" fill={COLORS.cyan} />

          {/* Windshield */}
          <rect x="133" y="132" width="11" height="8" rx="2" fill={COLORS.dark} opacity={0.7} />
          <rect x="147" y="132" width="11" height="8" rx="2" fill={COLORS.dark} opacity={0.7} />

          {/* Spoiler */}
          <rect x="118" y="136" width="12" height="3" rx="1" fill={COLORS.purpleDark} />

          {/* Wheels */}
          <g style={{ animation: `${wheelSpin} 0.3s linear infinite` }}>
            <circle cx="132" cy="156" r="6" fill="#333" />
            <circle cx="132" cy="156" r="3" fill="#666" />
            <line x1="132" y1="150" x2="132" y2="162" stroke="#555" strokeWidth="1" />
          </g>
          <g style={{ animation: `${wheelSpin} 0.3s linear infinite` }}>
            <circle cx="163" cy="156" r="6" fill="#333" />
            <circle cx="163" cy="156" r="3" fill="#666" />
            <line x1="163" y1="150" x2="163" y2="162" stroke="#555" strokeWidth="1" />
          </g>

          {/* Exhaust flame */}
          <ellipse cx="113" cy="148" rx="6" ry="3" fill={COLORS.orange} opacity={0.7} />
          <ellipse cx="110" cy="148" rx="4" ry="2" fill={COLORS.pink} opacity={0.5} />
        </g>

        {/* Speed lines behind car */}
        {[140, 145, 150, 155].map((y, i) => (
          <line
            key={i}
            x1="100"
            y1={y}
            x2="130"
            y2={y}
            stroke={COLORS.cyan}
            strokeWidth="1.5"
            opacity={0.4}
            style={{
              animation: `${speedLine} 0.8s ease-out ${i * 0.15}s infinite`,
            }}
          />
        ))}

        {/* Neon "LOADING" progress bar at bottom */}
        <g style={{ animation: `${neonFlicker} 3s ease-in-out infinite` }}>
          {/* Bar background */}
          <rect x="80" y="172" width="140" height="6" rx="3" fill={COLORS.dark} />
          {/* Bar fill — uses SVG animate, conditionally rendered for reduced motion */}
          <rect x="80" y="172" height="6" rx="3" fill={COLORS.purple}
            width={prefersReducedMotion ? 70 : undefined}>
            {!prefersReducedMotion && (
              <animate
                attributeName="width"
                values="0;120;0"
                dur="3.5s"
                repeatCount="indefinite"
              />
            )}
          </rect>
          {/* Bar glow */}
          <rect x="80" y="172" height="6" rx="3" fill={COLORS.purpleLight} opacity={0.3}
            width={prefersReducedMotion ? 70 : undefined}>
            {!prefersReducedMotion && (
              <animate
                attributeName="width"
                values="0;120;0"
                dur="3.5s"
                repeatCount="indefinite"
              />
            )}
          </rect>
        </g>

        {/* XP counter */}
        <text
          x="150"
          y="25"
          textAnchor="middle"
          fill={COLORS.purple}
          fontSize="11"
          fontWeight="700"
          fontFamily="'IBM Plex Sans', sans-serif"
          opacity={prefersReducedMotion ? 0.7 : undefined}
        >
          +500 XP
          {!prefersReducedMotion && (
            <animate
              attributeName="opacity"
              values="0.4;1;0.4"
              dur="2s"
              repeatCount="indefinite"
            />
          )}
        </text>
      </svg>

      {/* Loading text */}
      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          color: COLORS.purpleText,
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontSize: '0.8rem',
          animation: `${textGlow} 2s ease-in-out infinite`,
        }}
      >
        {message}
      </Typography>
    </SceneContainer>
  );
};

export const GamingLoadingAnimation = React.memo(GamingLoadingAnimationBase);
